import React, { useEffect } from 'react';

/**
 * GA4Loader — GDPR-compliant Google Analytics 4 injector.
 *
 * Loads `gtag.js` for the configured Measurement ID only when the user
 * has explicitly granted `analytics` consent via the site's cookie
 * banner (which stores `araia_cookie_consent` in localStorage and
 * dispatches an `araia:consent-change` event on every change).
 *
 * Configuration:
 *   REACT_APP_GA4_ID = "G-XXXXXXXXXX"   (Measurement ID from GA4)
 *
 * Behaviour:
 *   - No env var          → component is inert, no script injected
 *   - Env var + no consent → waits, listens for consent event
 *   - Env var + consent    → injects gtag.js once, calls `gtag('config',
 *                            id, { anonymize_ip: true })` and marks the
 *                            window flag so we never inject twice.
 *
 * On consent revocation (user picks "only technical") we cannot fully
 * unload the script but we set `window['ga-disable-<ID>'] = true`
 * which is Google's official kill-switch that stops data collection
 * effective immediately. See https://developers.google.com/analytics/
 * devguides/collection/gtagjs/user-opt-out
 */

const GA4_ID = process.env.REACT_APP_GA4_ID;
const INJECTED_FLAG = '__araia_ga4_loaded';

function loadGtag(id) {
    if (typeof window === 'undefined') return;
    if (window[INJECTED_FLAG]) return;
    // Turn off the kill-switch if a previous session had it on.
    try { window[`ga-disable-${id}`] = false; } catch (e) { /* noop */ }
    // dataLayer + gtag stub — must exist before the script loads.
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', id, {
        anonymize_ip: true,
        // We fire consent-gated so we don't need Google's own consent-mode.
        // Anonymised IP + no ads personalisation keeps us in GDPR-safe zone.
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
    });
    // Inject the loader script tag async — non-blocking.
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
    window[INJECTED_FLAG] = true;
}

function disableGtag(id) {
    if (typeof window === 'undefined') return;
    try { window[`ga-disable-${id}`] = true; } catch (e) { /* noop */ }
}

function hasAnalyticsConsent() {
    try {
        const raw = localStorage.getItem('araia_cookie_consent');
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return !!(parsed && parsed.analytics);
    } catch (e) {
        return false;
    }
}

export default function GA4Loader() {
    useEffect(() => {
        if (!GA4_ID) return; // dev / not configured yet — stay inert
        // Initial check on mount.
        if (hasAnalyticsConsent()) loadGtag(GA4_ID);
        // React to consent changes without needing a page reload.
        const onConsent = (e) => {
            const analytics = !!(e?.detail?.analytics);
            if (analytics) loadGtag(GA4_ID);
            else disableGtag(GA4_ID);
        };
        window.addEventListener('araia:consent-change', onConsent);
        return () => window.removeEventListener('araia:consent-change', onConsent);
    }, []);
    return null;
}

export { GA4_ID };
